/**
 * Formats a subSkill string into a readable title.
 * Example: 'breuken_vergelijken' -> 'Breuken Vergelijken'
 * Example: 'omtrek' -> 'Omtrek'
 */
export function formatSubSkill(subSkill: string): string {
  if (!subSkill) return ''

  // Replace underscores and hyphens with spaces
  const withSpaces = subSkill.replace(/[_-]/g, ' ')

  // Capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Formats a skill string into a readable title.
 * Example: 'breuken' -> 'Breuken'
 * Example: 'basisrekenen' -> 'Basisrekenen'
 * Example: 'getallenleer' -> 'Getallenleer'
 */
export function formatSkill(skill: string): string {
  if (!skill) return ''

  // Replace underscores and hyphens with spaces
  const withSpaces = skill.replace(/[_-]/g, ' ')

  // Capitalize first letter of each word
  return withSpaces
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Formats a timestamp into a relative Dutch distance string.
 * Example: "Vandaag", "Gisteren", "3 dagen geleden", "Vorige week"
 */
export function formatDistanceToNow(timestamp: number): string {
  const now = Date.now()
  const diffMs = now - timestamp
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMinutes < 1) return 'Zojuist'
  if (diffMinutes < 60) return `${diffMinutes} min geleden`
  if (diffHours < 24) {
    // Check if it's still "today"
    const nowDate = new Date(now)
    const tsDate = new Date(timestamp)
    if (nowDate.getDate() === tsDate.getDate()) return 'Vandaag'
    return 'Gisteren'
  }
  if (diffDays === 1) return 'Gisteren'
  if (diffDays < 7) return `${diffDays} dagen geleden`
  if (diffDays < 14) return 'Vorige week'
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weken geleden`
  return `${Math.floor(diffDays / 30)} maanden geleden`
}

/**
 * Maps a mastery percentage to a human-readable Dutch context label.
 * Used in parent/teacher portals to give meaning to raw percentages.
 */
export function getMasteryLabel(mastery: number): {
  label: string
  colorClass: string
} {
  if (mastery >= 90)
    return { label: 'Uitstekend', colorClass: 'text-emerald-600' }
  if (mastery >= 75)
    return { label: 'Goed op weg', colorClass: 'text-emerald-500' }
  if (mastery >= 60) return { label: 'Op schema', colorClass: 'text-sky-600' }
  if (mastery >= 40)
    return { label: 'In ontwikkeling', colorClass: 'text-amber-600' }
  if (mastery >= 20)
    return { label: 'Nog veel te leren', colorClass: 'text-orange-600' }
  return { label: 'Net begonnen', colorClass: 'text-slate-500' }
}
